import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Home,
  Briefcase,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  Shield,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Grid3X3,
  Users,
  List,
  FileText,
  Building2,
  History,
  TrendingDown,
  CreditCard,
  Key,
  Mail,
  Settings,
  BarChart3,
  Wallet,
  Lock,
} from 'lucide-react';
import { PERMISSIONS } from '../constants/permissions';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SubItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  permission?: string;
  permissionAny?: string[];
  ownerOnly?: boolean;
}

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactElement;
  permission?: string;
  permissionAny?: string[];
  subItems?: SubItem[];
}

// ─── Navigation data ─────────────────────────────────────────────────────────

const navItems: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: <Home className="w-[18px] h-[18px]" />,
  },
  {
    path: '/project-monitoring',
    label: 'Control de Proyectos',
    icon: <BarChart3 className="w-[18px] h-[18px]" />,
    permission: PERMISSIONS.PROYECTOS.VER,
  },
  {
    path: '/projects',
    label: 'Proyectos',
    icon: <Briefcase className="w-[18px] h-[18px]" />,
    permissionAny: [
      PERMISSIONS.PROYECTOS.VER,
      PERMISSIONS.UNIDADES.VER,
      PERMISSIONS.UNIDADES.EDITAR,
    ],
    subItems: [
      { path: '/projects', label: 'Mis Proyectos', icon: <List className="w-4 h-4" />, permission: PERMISSIONS.PROYECTOS.VER },
      { path: '/functional-units', label: 'Unidades Funcionales', icon: <Grid3X3 className="w-4 h-4" />, permissionAny: [PERMISSIONS.UNIDADES.VER, PERMISSIONS.UNIDADES.EDITAR] },
    ],
  },
  {
    path: '/commercial',
    label: 'Comercial',
    icon: <DollarSign className="w-[18px] h-[18px]" />,
    permissionAny: [
      PERMISSIONS.COMERCIAL.CLIENTES_VER,
      PERMISSIONS.COMERCIAL.PRECIOS_VER,
      PERMISSIONS.COMERCIAL.VENTAS_VER,
      PERMISSIONS.COMERCIAL.VENTAS_CREAR,
    ],
    subItems: [
      { path: '/clients', label: 'Clientes', icon: <Users className="w-4 h-4" />, permission: PERMISSIONS.COMERCIAL.CLIENTES_VER },
      { path: '/pricelists', label: 'Listas de Precios', icon: <List className="w-4 h-4" />, permission: PERMISSIONS.COMERCIAL.PRECIOS_VER },
      { path: '/sales', label: 'Nueva Venta', icon: <DollarSign className="w-4 h-4" />, permission: PERMISSIONS.COMERCIAL.VENTAS_CREAR },
      { path: '/sales-history', label: 'Historial de Ventas', icon: <FileText className="w-4 h-4" />, permission: PERMISSIONS.COMERCIAL.VENTAS_VER },
    ],
  },
  {
    path: '/suppliers',
    label: 'Compras',
    icon: <ShoppingCart className="w-[18px] h-[18px]" />,
    permissionAny: [
      PERMISSIONS.COMPRAS.PROVEEDORES_VER,
      PERMISSIONS.COMPRAS.OC_VER,
      PERMISSIONS.COMPRAS.FACTURAS_VER,
    ],
    subItems: [
      { path: '/suppliers', label: 'Proveedores', icon: <Building2 className="w-4 h-4" />, permission: PERMISSIONS.COMPRAS.PROVEEDORES_VER },
      { path: '/purchase-orders', label: 'Órdenes de Compra', icon: <ShoppingCart className="w-4 h-4" />, permission: PERMISSIONS.COMPRAS.OC_VER },
      { path: '/supplier-invoices', label: 'Facturas de Proveedores', icon: <FileText className="w-4 h-4" />, permission: PERMISSIONS.COMPRAS.FACTURAS_VER },
    ],
  },
  {
    path: '/accounting',
    label: 'Contabilidad',
    icon: <TrendingUp className="w-[18px] h-[18px]" />,
    permissionAny: [
      PERMISSIONS.CONTABILIDAD.COBRANZAS_VER,
      PERMISSIONS.CONTABILIDAD.MODULO_VER,
      PERMISSIONS.CONTABILIDAD.CAJAS_VER,
      PERMISSIONS.CONTABILIDAD.TRANSACCIONES_VER,
      PERMISSIONS.CONTABILIDAD.CUENTAS_CORRIENTES_VER,
      PERMISSIONS.CONTABILIDAD.REPORTES_VER,
    ],
    subItems: [
      { path: '/customer-payments', label: 'Cobranzas', icon: <CreditCard className="w-4 h-4" />, permission: PERMISSIONS.CONTABILIDAD.COBRANZAS_VER },
      { path: '/customer-accounts', label: 'Cuentas Corrientes', icon: <Wallet className="w-4 h-4" />, permission: PERMISSIONS.CONTABILIDAD.CUENTAS_CORRIENTES_VER },
      { path: '/reports', label: 'Reportes', icon: <BarChart3 className="w-4 h-4" />, permission: PERMISSIONS.CONTABILIDAD.REPORTES_VER },
      { path: '/accounting', label: 'Libro Contable', icon: <FileText className="w-4 h-4" />, permission: PERMISSIONS.CONTABILIDAD.MODULO_VER },
      { path: '/cashflow', label: 'Flujo de Caja', icon: <TrendingDown className="w-4 h-4" />, permission: PERMISSIONS.CONTABILIDAD.CAJAS_VER },
      { path: '/transaction-history', label: 'Historial de Movimientos', icon: <History className="w-4 h-4" />, permission: PERMISSIONS.CONTABILIDAD.TRANSACCIONES_VER },
    ],
  },
  {
    path: '/users',
    label: 'Administración',
    icon: <Shield className="w-[18px] h-[18px]" />,
    subItems: [
      { path: '/users', label: 'Usuarios', icon: <Users className="w-4 h-4" /> },
      { path: '/roles', label: 'Roles y Permisos', icon: <Key className="w-4 h-4" />, permissionAny: [PERMISSIONS.USUARIOS_ROLES.ROLES_VER, PERMISSIONS.USUARIOS_ROLES.ROLES_GESTIONAR] },
      { path: '/invitations', label: 'Invitaciones', icon: <Mail className="w-4 h-4" />, ownerOnly: true },
      { path: '/config', label: 'Configuración', icon: <Settings className="w-4 h-4" />, permission: PERMISSIONS.CONFIGURACION.VER },
    ],
  },
];

// ─── Props ────────────────────────────────────────────────────────────────────

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
}

// ─── NavLink (expanded) ───────────────────────────────────────────────────────

interface NavLinkProps {
  to: string;
  icon: React.ReactElement;
  label: string;
  isActive: boolean;
  locked?: boolean;
  onClick?: () => void;
  sub?: boolean;
}

const SideNavLink: React.FC<NavLinkProps> = ({ to, icon, label, isActive, locked, onClick, sub }) => {
  if (locked) {
    return (
      <div
        title="Sin permiso"
        className={[
          'flex items-center gap-3 rounded-lg text-sm font-medium cursor-not-allowed select-none',
          sub ? 'py-1.5 px-3 ml-7' : 'py-2.5 px-3',
          'text-muted-foreground/35',
        ].join(' ')}
      >
        {!sub && (
          <span className="flex-shrink-0 text-muted-foreground/25">
            {icon}
          </span>
        )}
        {sub && (
          <span className="w-1 h-1 rounded-full flex-shrink-0 bg-muted-foreground/20" />
        )}
        <span className="truncate flex-1">{label}</span>
        <Lock className="w-3 h-3 flex-shrink-0 text-muted-foreground/30" />
      </div>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      className={[
        'group flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-150 outline-none',
        sub ? 'py-1.5 px-3 ml-7' : 'py-2.5 px-3',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      ].join(' ')}
    >
      {!sub && (
        <span className={['flex-shrink-0', isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'].join(' ')}>
          {icon}
        </span>
      )}
      {sub && (
        <span className={['w-1 h-1 rounded-full flex-shrink-0', isActive ? 'bg-primary' : 'bg-muted-foreground/40 group-hover:bg-foreground/40'].join(' ')} />
      )}
      <span className="truncate">{label}</span>
      {isActive && !sub && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />}
    </Link>
  );
};

// ─── IconLink (collapsed) ─────────────────────────────────────────────────────

interface IconLinkProps {
  to: string;
  icon: React.ReactElement;
  label: string;
  isActive: boolean;
  locked?: boolean;
  onClick?: () => void;
}

const IconLink: React.FC<IconLinkProps> = ({ to, icon, label, isActive, locked, onClick }) => {
  if (locked) {
    return (
      <div
        title={`${label} — Sin permiso`}
        aria-label={label}
        className="relative flex items-center justify-center w-10 h-10 rounded-lg mx-auto cursor-not-allowed text-muted-foreground/25"
      >
        {icon}
        <span className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center w-3.5 h-3.5 rounded-full bg-card border border-border">
          <Lock className="w-2 h-2 text-muted-foreground/40" />
        </span>
      </div>
    );
  }

  return (
    <Link
      to={to}
      onClick={onClick}
      title={label}
      aria-label={label}
      className={[
        'flex items-center justify-center w-10 h-10 rounded-lg mx-auto transition-all duration-150 outline-none',
        isActive
          ? 'bg-primary/10 text-primary'
          : 'text-muted-foreground hover:bg-accent hover:text-foreground',
      ].join(' ')}
    >
      {icon}
    </Link>
  );
};

// ─── AccordionNav (expanded) ──────────────────────────────────────────────────

interface AccordionNavProps {
  item: NavItem;
  isActive: boolean;
  isExpanded: boolean;
  allLocked: boolean;
  onToggle: () => void;
  onLinkClick: () => void;
  currentPath: string;
  checkAccess: (s: SubItem) => boolean;
}

const AccordionNav: React.FC<AccordionNavProps> = ({
  item, isActive, isExpanded, allLocked, onToggle, onLinkClick, currentPath, checkAccess,
}) => (
  <li>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isExpanded}
      className={[
        'group flex items-center w-full gap-3 rounded-lg transition-all duration-150 py-2.5 px-3',
        allLocked
          ? 'opacity-50 cursor-default'
          : isActive
            ? 'bg-primary/10'
            : 'hover:bg-accent',
      ].join(' ')}
    >
      <span className={[
        'flex-shrink-0',
        allLocked
          ? 'text-muted-foreground/40'
          : isActive
            ? 'text-primary'
            : 'text-muted-foreground group-hover:text-foreground',
      ].join(' ')}>
        {item.icon}
      </span>
      <span className={[
        'flex-1 truncate text-sm font-medium text-left',
        allLocked
          ? 'text-muted-foreground/40'
          : isActive
            ? 'text-primary'
            : 'text-muted-foreground group-hover:text-foreground',
      ].join(' ')}>
        {item.label}
      </span>
      {allLocked
        ? <Lock className="w-3 h-3 flex-shrink-0 text-muted-foreground/30" />
        : (
          <ChevronDown className={[
            'w-3.5 h-3.5 flex-shrink-0 transition-transform duration-200',
            isExpanded ? 'rotate-180' : '',
            isActive ? 'text-primary' : 'text-muted-foreground/60 group-hover:text-foreground',
          ].join(' ')} />
        )
      }
    </button>

    {!allLocked && (
      <div className={['overflow-hidden transition-all duration-200 ease-in-out', isExpanded ? 'max-h-96 opacity-100 mt-0.5' : 'max-h-0 opacity-0'].join(' ')}>
        <ul className="space-y-0.5 pb-1 pt-0.5">
          {item.subItems!.map((sub) => (
            <li key={sub.path}>
              <SideNavLink
                to={sub.path}
                icon={sub.icon}
                label={sub.label}
                isActive={currentPath === sub.path}
                locked={!checkAccess(sub)}
                onClick={onLinkClick}
                sub
              />
            </li>
          ))}
        </ul>
      </div>
    )}
  </li>
);

// ─── Sidebar ─────────────────────────────────────────────────────────────────

export const Sidebar: React.FC<SidebarProps> = ({ open, onClose, collapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { hasPermission, hasAnyPermission, isOwner } = useAuth();

  const navRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    try {
      const saved = sessionStorage.getItem('sidebar_scroll');
      if (saved && navRef.current) navRef.current.scrollTop = parseInt(saved, 10);
    } catch {}
  }, []);

  const handleNavScroll = () => {
    try {
      if (navRef.current) sessionStorage.setItem('sidebar_scroll', String(navRef.current.scrollTop));
    } catch {}
  };

  const [expandedMenus, setExpandedMenus] = React.useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('sidebar_expanded_menus');
      if (saved) return new Set(JSON.parse(saved) as string[]);
    } catch {}
    return new Set(['Proyectos', 'Comercial', 'Compras']);
  });

  React.useEffect(() => {
    try {
      localStorage.setItem('sidebar_expanded_menus', JSON.stringify(Array.from(expandedMenus)));
    } catch {}
  }, [expandedMenus]);

  React.useEffect(() => {
    navItems.forEach((item) => {
      if (item.subItems?.some((s) => location.pathname === s.path)) {
        setExpandedMenus((prev) => new Set(prev).add(item.label));
      }
    });
  }, [location.pathname]);

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  // Returns true if user has access to this item
  const checkAccess = (item: { permission?: string; permissionAny?: string[]; ownerOnly?: boolean }): boolean => {
    if (item.ownerOnly && !isOwner()) return false;
    if (item.permission && !hasPermission(item.permission)) return false;
    if (item.permissionAny?.length && !hasAnyPermission(...item.permissionAny)) return false;
    return true;
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <aside
        aria-label="Navegación principal"
        className={[
          'fixed top-0 left-0 bottom-0 z-50 flex flex-col',
          'bg-card border-r border-border',
          'transition-[width,transform] duration-300 ease-in-out overflow-hidden',
          collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64',
          'w-64',
          'lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        {/* ── Brand ─────────────────────────────────────────────── */}
        <div
          className={[
            'flex items-center border-b border-border flex-shrink-0 transition-all duration-300',
            collapsed ? 'justify-center px-0 py-5' : 'gap-3 px-5 py-5',
          ].join(' ')}
        >
          <button
            type="button"
            onClick={onToggleCollapse}
            className="hidden lg:flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0 hover:opacity-80 transition-opacity"
            aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            title={collapsed ? 'Expandir' : 'Colapsar'}
          >
            <img src="/NewLogo.ico" alt="PruneStone" className="w-9 h-9 rounded-xl object-contain" />
          </button>

          <div className="lg:hidden flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0">
            <img src="/NewLogo.ico" alt="PruneStone" className="w-9 h-9 rounded-xl object-contain" />
          </div>

          <div className={['min-w-0 flex-1 transition-all duration-200', collapsed ? 'lg:hidden' : ''].join(' ')}>
            <p className="text-base font-bold text-foreground leading-tight tracking-tight">PruneStone</p>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">Sistema de Gestión</p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="lg:hidden flex items-center justify-center w-7 h-7 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors flex-shrink-0"
            aria-label="Cerrar menú"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* ── Navigation ────────────────────────────────────────── */}
        <nav
          ref={navRef}
          onScroll={handleNavScroll}
          className={[
            'flex-1 overflow-y-auto overflow-x-hidden scrollbar-custom',
            'py-4',
            collapsed ? 'px-1' : 'px-4',
          ].join(' ')}
        >
          {!collapsed && (
            <p className="px-3 mb-3 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/50 select-none">
              Navegación
            </p>
          )}

          {/* Collapsed: icon-only */}
          {collapsed && (
            <ul className="space-y-1 flex flex-col items-center">
              {navItems.map((item) => {
                const accessible = checkAccess(item);
                const subAccessible = item.subItems ? item.subItems.some(checkAccess) : true;
                const locked = !accessible && !subAccessible;
                const isActive =
                  location.pathname === item.path ||
                  item.subItems?.some((s) => location.pathname === s.path) ||
                  false;
                return (
                  <li key={item.path} className="w-full flex justify-center">
                    <IconLink
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      isActive={isActive}
                      locked={locked}
                      onClick={locked ? undefined : onClose}
                    />
                  </li>
                );
              })}
            </ul>
          )}

          {/* Expanded: accordion */}
          {!collapsed && (
            <ul className="space-y-0.5">
              {navItems.map((item) => {
                const isActive =
                  location.pathname === item.path ||
                  item.subItems?.some((s) => location.pathname === s.path) ||
                  false;

                if (item.subItems && item.subItems.length > 0) {
                  const allLocked = !item.subItems.some(checkAccess);
                  return (
                    <AccordionNav
                      key={item.label}
                      item={item}
                      isActive={isActive}
                      isExpanded={expandedMenus.has(item.label)}
                      allLocked={allLocked}
                      onToggle={() => !allLocked && toggleMenu(item.label)}
                      onLinkClick={onClose}
                      currentPath={location.pathname}
                      checkAccess={checkAccess}
                    />
                  );
                }

                const locked = !checkAccess(item);
                return (
                  <li key={item.path}>
                    <SideNavLink
                      to={item.path}
                      icon={item.icon}
                      label={item.label}
                      isActive={isActive}
                      locked={locked}
                      onClick={locked ? undefined : onClose}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        {/* ── Collapse toggle ────────────────────────────────────── */}
        <div
          className={[
            'hidden lg:flex border-t border-border flex-shrink-0',
            collapsed ? 'justify-center px-0 py-3' : 'justify-end px-4 py-3',
          ].join(' ')}
        >
          <button
            type="button"
            onClick={onToggleCollapse}
            className="flex items-center justify-center w-8 h-8 rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
            aria-label={collapsed ? 'Expandir sidebar' : 'Colapsar sidebar'}
            title={collapsed ? 'Expandir' : 'Colapsar'}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>
      </aside>
    </>
  );
};
